import React, { useState } from 'react';
import { Check, X } from 'lucide-react';
import {
  PendingEntry,
  ChangeProposal,
  PendingEntryStatus,
  ReviewRequest,
} from '../../types/workflow';
import { workflowService } from '../../services/workflowService';
import { Button, Dialog, DialogBody, DialogFooter, DialogHeader, Field, SectionHeader, Textarea } from '../ui';
import { DiffView } from './DiffView';
import { getEntityName, getEntityTypeLabel, isReviewable, StatusPill } from './workflowFormat';

interface ReviewInterfaceProps {
  entry?: PendingEntry;
  proposal?: ChangeProposal;
  onReviewComplete?: () => void;
  onClose?: () => void;
}

const noop = () => undefined;

export const ReviewInterface: React.FC<ReviewInterfaceProps> = ({
  entry,
  proposal,
  onReviewComplete,
  onClose,
}) => {
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState<PendingEntryStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  const item = entry || proposal;
  if (!item) {
    return null;
  }

  const isEntry = 'entityData' in item;
  const isProposal = 'originalData' in item;

  const handleReview = async (status: PendingEntryStatus.APPROVED | PendingEntryStatus.REJECTED) => {
    try {
      setSubmitting(status);
      setError(null);

      const reviewRequest: ReviewRequest = {
        status,
        reviewNotes: reviewNotes.trim() || undefined,
      };

      if (isEntry && entry) {
        await workflowService.reviewPendingEntry(entry.id, reviewRequest);
      } else if (isProposal && proposal) {
        await workflowService.reviewChangeProposal(proposal.id, reviewRequest);
      }

      onReviewComplete?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit review');
    } finally {
      setSubmitting(null);
    }
  };

  const entityName =
    isEntry && entry
      ? getEntityName(entry.entityType, entry.entityData)
      : isProposal && proposal
        ? getEntityName(proposal.entityType, proposal.originalData)
        : 'Unknown Entity';

  const renderEntityData = (data: any) => {
    if (!data || typeof data !== 'object') {
      return <p className="px-4 py-3 text-[13px] text-neutral-500">No data available</p>;
    }

    return (
      <dl className="divide-y divide-neutral-200/70">
        {Object.entries(data).map(([key, value]) => {
          if (value === null || value === undefined || value === '') {
            return null;
          }

          const displayKey = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
          let displayValue: string;
          const primitiveList =
            Array.isArray(value) && value.every(v => v === null || typeof v !== 'object');

          if (primitiveList) {
            displayValue = (value as unknown[]).join(', ');
          } else if (typeof value === 'boolean') {
            displayValue = value ? 'Yes' : 'No';
          } else if (typeof value === 'object') {
            displayValue = JSON.stringify(value, null, 2);
          } else {
            displayValue = String(value);
          }

          return (
            <div key={key} className="grid grid-cols-[minmax(96px,1fr)_2fr] gap-3 px-4 py-2.5 text-[13px]">
              <dt className="text-neutral-500">{displayKey}</dt>
              <dd className="min-w-0 break-words text-ink">
                {typeof value === 'object' && !primitiveList ? (
                  <pre className="whitespace-pre-wrap break-words rounded-lg bg-neutral-50 px-2.5 py-2 font-ui-mono text-[12px] text-neutral-600">
                    {displayValue}
                  </pre>
                ) : (
                  displayValue
                )}
              </dd>
            </div>
          );
        })}
      </dl>
    );
  };

  const submittedBy = isEntry ? entry?.submitter?.email : proposal?.proposer?.email || 'Unknown';
  const submittedDate = new Date(isEntry ? entry!.submittedAt : proposal!.proposedAt);
  const submittedAt = submittedDate.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(submittedDate.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}),
    hour: '2-digit',
    minute: '2-digit',
  });
  const reviewable = isReviewable(item.status);
  const changeCount =
    isProposal && proposal?.proposedChanges && typeof proposal.proposedChanges === 'object'
      ? Object.keys(proposal.proposedChanges).length
      : 0;

  const facts: Array<[string, React.ReactNode]> = [
    ['Status', <StatusPill key="status" status={item.status} />],
    [isEntry ? 'Submitted by' : 'Proposed by', submittedBy || 'Unknown'],
    ['When', <span key="when" className="tabular-nums">{submittedAt}</span>],
    ['Assigned', item.assignee?.email || 'Unassigned'],
  ];

  return (
    <Dialog
      open
      onClose={submitting ? noop : onClose ?? noop}
      labelledBy="review-title"
      handle
      className="max-h-[92vh] sm:max-h-[86vh] sm:max-w-2xl"
    >
      <DialogHeader
        id="review-title"
        eyebrow={`${isEntry ? 'New' : 'Change'} · ${getEntityTypeLabel(item.entityType)}`}
        title={entityName}
        onClose={onClose}
      />

      <DialogBody className="space-y-5">
        {/* Submission details */}
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-2xl bg-neutral-50 px-4 py-3 sm:grid-cols-[auto_minmax(0,1.5fr)_auto_minmax(0,1fr)]">
          {facts.map(([label, value]) => (
            <div key={label} className="min-w-0">
              <dt className="text-[12px] text-neutral-400">{label}</dt>
              <dd className="mt-0.5 truncate text-[13px] text-ink" title={typeof value === 'string' ? value : undefined}>
                {value}
              </dd>
            </div>
          ))}
        </dl>

        {/* Content */}
        {isEntry && entry ? (
          <section>
            <SectionHeader title="Entry" />
            <div className="overflow-hidden rounded-2xl border border-neutral-200">{renderEntityData(entry.entityData)}</div>
          </section>
        ) : isProposal && proposal ? (
          <section>
            <SectionHeader
              title="Changes"
              aside={changeCount > 0 ? `${changeCount} field${changeCount !== 1 ? 's' : ''}` : undefined}
            />
            <DiffView proposal={proposal} />
          </section>
        ) : null}

        {item.reviewNotes && !reviewable && (
          <section>
            <SectionHeader title="Notes" />
            <p className="whitespace-pre-wrap rounded-2xl bg-neutral-50 px-4 py-3 text-[13px] text-ink">{item.reviewNotes}</p>
          </section>
        )}

        {/* Review notes */}
        {reviewable && (
          <Field label="Notes" htmlFor="reviewNotes">
            <Textarea
              id="reviewNotes"
              rows={3}
              value={reviewNotes}
              onChange={e => setReviewNotes(e.target.value)}
              placeholder="Optional"
            />
          </Field>
        )}

        {error && (
          <p role="alert" className="rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
            {error}
          </p>
        )}
      </DialogBody>

      <DialogFooter>
        {reviewable ? (
          <>
            <Button variant="ghost" onClick={onClose} disabled={!!submitting} className="mr-auto hidden sm:inline-flex">
              Cancel
            </Button>
            <Button
              variant="secondary"
              onClick={() => handleReview(PendingEntryStatus.REJECTED)}
              disabled={!!submitting}
              loading={submitting === PendingEntryStatus.REJECTED}
              className="flex-1 sm:flex-none"
            >
              {submitting !== PendingEntryStatus.REJECTED && <X className="h-4 w-4" strokeWidth={1.75} />}
              {submitting === PendingEntryStatus.REJECTED ? 'Rejecting' : 'Reject'}
            </Button>
            <Button
              variant="primary"
              onClick={() => handleReview(PendingEntryStatus.APPROVED)}
              disabled={!!submitting}
              loading={submitting === PendingEntryStatus.APPROVED}
              className="flex-1 sm:flex-none"
            >
              {submitting !== PendingEntryStatus.APPROVED && <Check className="h-4 w-4" strokeWidth={1.75} />}
              {submitting === PendingEntryStatus.APPROVED ? 'Approving' : 'Approve'}
            </Button>
          </>
        ) : (
          <Button variant="secondary" onClick={onClose} className="flex-1 sm:flex-none">
            Close
          </Button>
        )}
      </DialogFooter>
    </Dialog>
  );
};
